<script setup lang="ts">
import { Navigation } from 'swiper/modules';
import type { Swiper as SwiperClass } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';
import type { NavigationOptions } from 'swiper/types';
import 'swiper/css';
import 'swiper/css/navigation';
import AdDisclosurePopover from '~/widgets/ad-disclosure/ui/AdDisclosurePopover.vue';
import logo from './images/logo.svg';
import book from './images/book.svg';
import territories from './images/territories.jpg';
import territories2x from './images/territories@2x.jpg';
import territories3x from './images/territories@3x.jpg';
import resorts from './images/resorts.jpg';
import resorts2x from './images/resorts@2x.jpg';
import resorts3x from './images/resorts@3x.jpg';
import hotels from './images/hotels.jpg';
import hotels2x from './images/hotels@2x.jpg';
import hotels3x from './images/hotels@3x.jpg';
import special from './images/special.jpg';
import special2x from './images/special@2x.jpg';
import special3x from './images/special@3x.jpg';
import entertainment from './images/entertainment.jpg';
import entertainment2x from './images/entertainment@2x.jpg';
import entertainment3x from './images/entertainment@3x.jpg';

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<{ placement?: 'main' | 'inner' }>(), {
  placement: 'main',
});

const ERID = '2SDnjeJgmW3';
const AD_ERID = `ERID ${ERID}`;
const AD_COMPANY =
  'Рекламодатель:<br />ООО «КОМОНВЕЛС ПАРТНЕРШИП»,<br />ИНН: 7705637585.';

const PROJECT_URL = 'https://cmwp.cre.ru/';

const ITEMS = [
  {
    key: 'territories',
    title: 'Туристские\nтерритории',
    description:
      'Дальний Восток • Байкал\nЗолотые пески России\nБольшой Кавказ',
    image: territories,
    imageSrcset: `${territories} 1x, ${territories2x} 2x, ${territories3x} 3x`,
    color: '#566d35',
    url: 'https://cmwp.cre.ru/projects/tourism',
  },
  {
    key: 'resorts',
    title: 'Курорты',
    description: 'Манжерок • Архыз\nКаспийский кластер\nСибирская монета',
    image: resorts,
    imageSrcset: `${resorts} 1x, ${resorts2x} 2x, ${resorts3x} 3x`,
    color: '#7c5b32',
    url: 'https://cmwp.cre.ru/projects/resorts',
  },
  {
    key: 'hotels',
    title: 'Отели,\nсанатории и СПА',
    description:
      'Камелия\nГостиница в Мурманске на месте «Шахтёра» • Домковка\nКондо-отель в Кабардинке',
    image: hotels,
    imageSrcset: `${hotels} 1x, ${hotels2x} 2x, ${hotels3x} 3x`,
    color: '#3d7672',
    url: 'https://cmwp.cre.ru/projects/hotels',
  },
  {
    key: 'special',
    title: 'Особые\nпроекты',
    description:
      'Гаспра • Автодром в Воронеже\nЗамок Понизовкина Абзаково-Банное',
    image: special,
    imageSrcset: `${special} 1x, ${special2x} 2x, ${special3x} 3x`,
    color: '#415064',
    url: 'https://cmwp.cre.ru/projects/special',
  },
  {
    key: 'entertainment',
    title: 'Развлекательные\nи спортивные\nцентры',
    description:
      'Сочи Парк • Парк Патриот\nАквапарк на острове Русский\nЯрославич',
    image: entertainment,
    imageSrcset: `${entertainment} 1x, ${entertainment2x} 2x, ${entertainment3x} 3x`,
    color: '#415064',
    url: 'https://cmwp.cre.ru/projects/centers',
  },
];

const withUtm = (url: string) =>
  `${url}?utm_source=cmwp${props.placement}&erid=${ERID}`;

const isAdDisclosureOpen = ref(false);

const isControlsVisible = ref(false);
const swiperInstance = shallowRef<SwiperClass | null>(null);
const prevEl = ref<HTMLElement | null>(null);
const nextEl = ref<HTMLElement | null>(null);
const navigationOptions = ref<NavigationOptions>({
  prevEl: null,
  nextEl: null,
});
const updateControlsVisibility = (swiper: SwiperClass) => {
  isControlsVisible.value = !swiper.isLocked;
};
const onSwiper = (swiper: SwiperClass) => {
  swiperInstance.value = swiper;
  updateControlsVisibility(swiper);
};
onMounted(() => {
  if (!prevEl.value || !nextEl.value) return;
  navigationOptions.value = { prevEl: prevEl.value, nextEl: nextEl.value };
  nextTick(() => {
    const swiper = swiperInstance.value;
    if (!swiper?.navigation) return;
    swiper.navigation.destroy();
    swiper.params.navigation = {
      ...(typeof swiper.params.navigation === 'object'
        ? swiper.params.navigation
        : {}),
      ...navigationOptions.value,
    };
    swiper.navigation.init();
    swiper.navigation.update();
    updateControlsVisibility(swiper);
  });
});
</script>

<template>
  <section v-bind="$attrs" :class="$style.cmwp" aria-label="Россия. Места силы">
    <header :class="$style.cmwp__header">
      <a
        :href="withUtm(PROJECT_URL)"
        target="_blank"
        rel="noopener noreferrer"
        :class="$style.cmwp__logoLink"
        aria-label="CMWP — Россия. Места силы"
      >
        <span
          :class="$style.cmwp__logo"
          :style="{ maskImage: `url(${logo})` }"
        />
      </a>
      <div v-show="isControlsVisible" :class="$style.cmwp__controls">
        <button
          ref="prevEl"
          type="button"
          :class="$style.cmwp__control"
          aria-label="Предыдущий материал"
        >
          <Icon name="my-icon:i-arrow-left" />
        </button>
        <button
          ref="nextEl"
          type="button"
          :class="$style.cmwp__control"
          aria-label="Следующий материал"
        >
          <Icon name="my-icon:i-arrow-right" />
        </button>
      </div>
    </header>
    <Swiper
      :class="$style.cmwp__slider"
      :modules="[Navigation]"
      :navigation="navigationOptions"
      :observer="true"
      :observe-parents="true"
      :grab-cursor="true"
      :watch-overflow="true"
      slides-per-view="auto"
      :slides-per-group="1"
      @swiper="onSwiper"
      @lock="updateControlsVisibility"
      @unlock="updateControlsVisibility"
    >
      <SwiperSlide :class="[$style.cmwp__slide, $style.cmwp__intro]">
        <h2 :class="$style.cmwp__headline">
          Как создаются<br />эффективные<br />туристские проекты
        </h2>
        <p :class="$style.cmwp__slogan">20 проектов · 11 часовых поясов</p>
        <a
          :href="withUtm(PROJECT_URL)"
          target="_blank"
          rel="noopener noreferrer"
          :class="$style.cmwp__open"
        >
          Открыть <img :src="book" alt="" />
        </a>
        <div
          :class="[
            $style.cmwp__adMark,
            { [$style.cmwp__adMark_open!]: isAdDisclosureOpen },
          ]"
        >
          <AdDisclosurePopover
            v-model:open="isAdDisclosureOpen"
            :erid="AD_ERID"
            :company="AD_COMPANY"
            variant="info"
            side="top"
            align="start"
            trigger-icon-color="white"
          />
          <span v-if="!isAdDisclosureOpen">Реклама</span>
        </div>
      </SwiperSlide>
      <SwiperSlide
        v-for="item in ITEMS"
        :key="item.key"
        :class="$style.cmwp__slide"
      >
        <a
          :href="withUtm(item.url)"
          target="_blank"
          rel="noopener noreferrer"
          :class="[$style.cmwp__card, $style[`cmwp__card_${item.key}`]]"
        >
          <img
            :src="item.image"
            :srcset="item.imageSrcset"
            width="270"
            height="378"
            :alt="item.title.replaceAll('\n', ' ')"
            :class="$style.cmwp__image"
            loading="lazy"
          />
          <div :class="$style.cmwp__text">
            <h3 :class="$style.cmwp__title" :style="{ color: item.color }">
              {{ item.title }}
            </h3>
            <p :class="$style.cmwp__description">{{ item.description }}</p>
          </div>
          <span :class="$style.cmwp__read">Читать</span>
        </a>
      </SwiperSlide>
    </Swiper>
  </section>
</template>

<style module lang="scss">
@font-face {
  font-family: 'CMWP Oranienbaum';
  src: url('./fonts/Oranienbaum-Regular.ttf') format('truetype');
  font-weight: 400;
  font-display: swap;
}
@font-face {
  font-family: 'CMWP Open Sans';
  src: url('./fonts/OpenSans-Regular.woff2') format('woff2');
  font-weight: 400;
  font-display: swap;
}
@font-face {
  font-family: 'CMWP Condensed';
  src: url('./fonts/DINCondensed-Regular.woff2') format('woff2');
  font-weight: 400;
  font-display: swap;
}
.cmwp {
  --cmwp-red: #d61336;
  overflow: hidden;
  background: var(--cmwp-red);
  color: var(--color-white);
  &,
  * {
    box-sizing: border-box;
  }
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 66px;
    border-bottom: 1px solid #ccc;
  }
  &__logoLink {
    display: block;
    min-width: 0;
    margin: 15px 37px;
  }
  &__logo {
    display: block;
    width: 332px;
    height: 35px;
    background: white;
    mask-repeat: no-repeat;
    mask-size: 332px 35px;
  }
  &__controls {
    display: flex;
    align-self: stretch;
    flex-shrink: 0;
    width: 200px;
  }
  &__control {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100px;
    min-height: 65px;
    padding: 0;
    border: 0;
    border-left: 1px solid #ccc;
    background: transparent;
    cursor: pointer;
    svg {
      width: 24px;
      height: 24px;
    }
  }
  &__control:global(.swiper-button-disabled) {
    cursor: default;
    svg {
      opacity: 0.45;
    }
  }
  &__slider {
    width: 100%;
    height: 378px;
  }
  &__slide {
    flex: 0 0 270px;
    width: 270px;
    height: 378px;
  }
  &__intro {
    container-type: inline-size;
    flex-basis: 489px;
    width: 489px;
    padding: 30px;
    background: url('./images/background.png') left top / 1080px 378px no-repeat
      var(--cmwp-red);
  }
  &__headline {
    margin: 0;
    font-family: 'CMWP Condensed', sans-serif;
    /* Подобрано так, чтобы «туристские проекты» влезали в одну строку на любой ширине */
    font-size: min(52px, calc((100cqw + 22px) / 8.3));
    font-weight: 700;
    line-height: 1.05;
    letter-spacing: -1.2546px;
    text-transform: uppercase;
    white-space: normal;
  }
  &__slogan {
    margin: 10px 0 24px;
    font-family: Georgia, serif;
    font-size: 18px;
    line-height: 1.2;
    letter-spacing: -0.261px;
    opacity: 0.8;
  }
  &__open {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 200px;
    height: 58px;
    border-radius: 100px;
    background: white;
    color: var(--cmwp-red);
    font-family: 'CMWP Open Sans', sans-serif;
    font-size: 18px;
    line-height: 27px;
    letter-spacing: 2px;
    text-transform: uppercase;
    text-decoration: none;
    img {
      width: 21.512px;
      height: 20.293px;
    }
  }
  /* Значок внутри AdDisclosurePopover позиционируется абсолютно */
  &__adMark {
    position: relative;
    display: flex;
    align-items: center;
    width: fit-content;
    height: 21px;
    margin-top: var(--space-2x);
    padding: 0 10px 0 28px;
    border-radius: 15px;
    background-color: rgba(255, 255, 255, 0.3);
    font-family: 'CMWP Open Sans', sans-serif;
    font-size: 9px;
    line-height: 1;
    letter-spacing: 1px;
    text-transform: uppercase;
    white-space: nowrap;

    :global(button) {
      top: 0;
      right: auto;
      left: 0;
      color: rgba(255, 255, 255, 0.45);

      &:hover {
        color: rgba(255, 255, 255, 0.7);
      }

      &[data-state='open'] {
        color: rgba(255, 255, 255, 0.85);
      }
    }

    &_open {
      width: 21px;
      padding: 0;
      background-color: transparent;
    }
  }
  &__card {
    position: relative;
    display: block;
    height: 100%;
    overflow: hidden;
    border-right: 1px solid #dcdcdc;
    background: white;
    font-family: 'CMWP Oranienbaum', Georgia, serif;
    text-decoration: none;
  }
  &__image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &__text {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 29px 17px 0;
    text-align: center;
  }
  &__title {
    margin: 0;
    font-size: 40px;
    line-height: 0.84;
    letter-spacing: -3px;
    font-weight: 400;
    white-space: pre-line;
  }
  &__description {
    margin: 0;
    color: #676968;
    font-size: 17px;
    line-height: 1.18;
    white-space: pre-line;
  }
  &__card_resorts &__text {
    gap: 48px;
  }
  &__card_entertainment &__title {
    width: 246px;
  }
  &__read {
    position: absolute;
    bottom: 20px;
    left: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 165px;
    height: 44px;
    transform: translateX(-50%);
    border-radius: 100px;
    background: var(--cmwp-red);
    color: white;
    font-size: 19.5px;
    line-height: 1;
    letter-spacing: 0.39px;
    transition: background-color 0.2s;
  }
  a[href] {
    cursor: pointer;
  }
  &__logoLink[href]:hover,
  &__open[href]:hover {
    opacity: 0.85;
  }
}
@media (hover: hover) {
  .cmwp__card:hover .cmwp__read {
    background-color: #e02a49;
  }
}
@media (max-width: 767px) {
  .cmwp {
    &__logoLink {
      margin: 16px;
    }
    &__logo {
      width: 250px;
      height: 27px;
      mask-size: contain;
    }
    &__intro {
      width: 420px;
      flex-basis: 420px;
      padding-left: 24px;
      padding-right: 24px;
    }
  }
}
@media (min-width: 480px) and (max-width: 767px) {
  .cmwp__headline {
    font-size: 46px;
  }
}
@media (min-width: 480px) and (max-width: 599px) {
  .cmwp {
    &__controls {
      width: 128px;
    }
    &__control {
      width: 64px;
    }
  }
}
@media (max-width: 479px) {
  .cmwp {
    &__header {
      flex-wrap: wrap;
    }
    &__logoLink {
      flex: 1 0 calc(100% - 32px);
    }
    &__logo {
      width: 332px;
      max-width: 100%;
      height: 35px;
    }
    &__controls {
      width: 100%;
      border-top: 1px solid #ccc;
    }
    &__control {
      width: 50%;
      min-height: 48px;
      &:first-child {
        border-left: 0;
      }
    }
    &__intro {
      width: 100%;
      flex-basis: 100%;
      padding: var(--space-2x);
    }
    &__slogan {
      font-size: 16px;
      margin-top: var(--space-2x);
      margin-bottom: var(--space-2-5x);
    }
  }
}
</style>
